import {Buffer} from 'buffer';
import isNil from 'lodash/isNil';
import {CodecTypes, ConnectedDevice, Pikaparam} from 'pikaparam';
import {TemperatureUnit} from 'puffco-api-axios-client';
import {
  EMPTY,
  catchError,
  distinctUntilChanged,
  filter,
  firstValueFrom,
  throwError,
  timeout,
} from 'rxjs';

import {Constants} from '../../../../constants';
import {
  ChamberType,
  DeviceSettings,
  LightingPattern,
  OperatingState,
} from '../../../types';
import {meetsMinimumFirmware} from '../../../utils';
import {bleLog} from '../../../utils/Logger';
import {Temperature} from '../../../utils/temperature';
import {ConnectionMetadata} from '../ConnectionMetadata';
import {Scratchpad, ScratchpadVersion} from '../Scratchpad';
import {
  AnyDeviceProperties,
  HeatCycleArrayIndex,
  LED_API_VERSIONS,
} from '../pikaparam';
import {
  Broadcast,
  DeviceAttributes,
  FirmwareType,
  IPeakProduct,
  PeakFeature,
  WriteLanternOptions,
  isMoodLight,
} from './IPeakDevice';
import {
  PeakLogCollector,
  StartPeakLogCollectorOptions,
} from './PeakLogCollector';
import {IUnitClient, UnitClient} from './UnitClient';
import {IUnitCycleAccess} from './UnitCycleAccess/IUnitCycleAccess';
import {UnitCycleAccess} from './UnitCycleAccess/UnitCycleAccess';
import {
  IUnitLedApi,
  UnitLedApiV1,
  UnitLedApiV2,
  UnitLedApiV3,
} from './UnitLedApi';

export interface PeakDeviceBaseOptions {
  device: ConnectedDevice;
  pikaparam: Pikaparam;
  firmwareType: FirmwareType;
  softwareRevision: string;
  metadata: ConnectionMetadata;
}

const observables = [
  'approxDabsRemaining',
  'battSoc',
  'battChargeEstTimeToFull',
  'battChargeState',
  'heaterType',
  'userHeaterTemp',
  'dabsPerDay',
  'faultLogEnd',
  'selectedHeatCycle',
  'lanternRemainingTime',
  'totalHeatCycles',
  'operatingState',
  'stateElapsedTime',
  'stateTotalTime',
  'heaterTempCommand',
] as const;

const setters = {
  // observable
  approxDabsRemaining: (a: Partial<DeviceAttributes>, value: number) => {
    a.approxDabsRemaining = value;
  },
  battSoc: (a: Partial<DeviceAttributes>, value: number) => {
    a.batteryLevel = Math.round(value);
  },
  battChargeEstTimeToFull: (a: Partial<DeviceAttributes>, value: number) => {
    a.chargeEstimatedTimeToFull = value;
  },
  battChargeState: (a: Partial<DeviceAttributes>, value: number) => {
    a.chargeState = value;
  },
  heaterType: (a: Partial<DeviceAttributes>, value: number) => {
    a.chamberType = value;
  },
  userHeaterTemp: (a: Partial<DeviceAttributes>, value: number) => {
    a.currentTemperature = Math.round(value);
  },
  dabsPerDay: (a: Partial<DeviceAttributes>, value: number) => {
    a.dabsPerDay = value;
  },
  faultLogEnd: (a: Partial<DeviceAttributes>, value: number) => {
    a.faultEndIndex = value;
  },
  selectedHeatCycle: (a: Partial<DeviceAttributes>, value: number) => {
    a.selectedHeatCycle = value;
  },
  lanternRemainingTime: (a: Partial<DeviceAttributes>, value: number) => {
    a.lanternTime = value;
  },
  totalHeatCycles: (a: Partial<DeviceAttributes>, value: number) => {
    a.totalHeatCycles = value;
  },
  operatingState: (a: Partial<DeviceAttributes>, value: OperatingState) => {
    a.operatingState = value;
  },
  stateTotalTime: (a: Partial<DeviceAttributes>, value: number) => {
    a.stateTotalTime = Number.isFinite(value) ? value : 0;
  },
  stateElapsedTime: (a: Partial<DeviceAttributes>, value: number) => {
    a.stateElapsedTime = Number.isFinite(value) ? value : 0;
  },
  heaterTempCommand: (a: Partial<DeviceAttributes>, value: number) => {
    a.targetTemperature = Math.round(value);
  },

  // static
  heatCycle0BoostTempDelta: (a: Partial<DeviceAttributes>, value: number) => {
    a.boostTemperature = value;
  },
  heatCycle0BoostTimeDelta: (a: Partial<DeviceAttributes>, value: number) => {
    a.boostTime = value;
  },
  ledBrightness: (a: Partial<DeviceAttributes>, value: Buffer) => {
    a.devBrightness = value;
  },
  readyModeCycle: (a: Partial<DeviceAttributes>, value: number) => {
    a.mode = value;
  },
  deviceBirthday: (a: Partial<DeviceAttributes>, value: number) => {
    a.dateOfBirth = value;
  },
  utcTime: (a: Partial<DeviceAttributes>, value: number) => {
    a.utcTime = value;
  },
  stealthMode: (a: Partial<DeviceAttributes>, value: number) => {
    a.stealth = value;
  },
  batteryCapacity: (a: Partial<DeviceAttributes>, value: number) => {
    a.batteryCapacity = value;
  },
  totalHeatCycleTime: (a: Partial<DeviceAttributes>, value: number) => {
    a.dabTotalTime = value;
  },
  userHeaterTempCommand: (a: Partial<DeviceAttributes>, value: number) => {
    a.targetTemperature = value;
  },
} as const;

export abstract class PeakDeviceBase<P extends AnyDeviceProperties> {
  static advertisedServices: string[] = [];

  name: string;
  attributes: Partial<DeviceAttributes> = {};

  readonly firmwareType: FirmwareType;
  readonly peripheralId: string;
  readonly softwareRevision: string;
  readonly serialNumber?: string;
  readonly modelNumber?: string;
  readonly product: IPeakProduct;
  readonly metadata: ConnectionMetadata;
  readonly leds: IUnitLedApi;
  readonly cycles: IUnitCycleAccess;

  euid?: string;
  gitHash?: string;
  broadcast?: Broadcast;

  protected readonly pikaparam: Pikaparam;
  protected readonly client: IUnitClient;
  protected readonly watchers: Map<
    keyof P,
    {clean: () => void; callbacks: Set<(p: string, value: unknown) => void>}
  > = new Map();

  private readonly device: ConnectedDevice;
  private readonly logCollector: PeakLogCollector;

  constructor({
    device,
    pikaparam,
    firmwareType,
    softwareRevision,
    metadata,
  }: PeakDeviceBaseOptions) {
    this.device = device;
    this.peripheralId = this.device.id;
    this.name = this.device.name ?? '';
    this.firmwareType = firmwareType;
    this.softwareRevision = softwareRevision;
    this.serialNumber = pikaparam.devInfo.info?.serialNumber;
    this.modelNumber = pikaparam.devInfo.info?.modelCode?.toString();
    this.product = {
      name: pikaparam.devInfo.info?.marketingName,
      type: pikaparam.devInfo.info?.product,
      consistent: !!pikaparam.devInfo.info?.isConsistent,
      features: pikaparam.devInfo.info?.features ?? [],
    };
    this.pikaparam = pikaparam;
    this.client = new UnitClient(this.pikaparam);

    const createBuilder = (ledApiVersion: 1 | 2 | 3): IUnitLedApi => {
      switch (ledApiVersion) {
        case 1:
          return new UnitLedApiV1(this.client);
        case 2:
          return new UnitLedApiV2(this.client);
        case 3:
          return new UnitLedApiV3(this.client);
      }
    };

    this.leds = createBuilder(this.getLedApiVersion());
    this.cycles = new UnitCycleAccess(
      this.softwareRevision,
      this.client,
      this.leds,
    );
    this.metadata = metadata;

    this.logCollector = new PeakLogCollector(pikaparam);
  }

  public get connected() {
    return this.device.state === 'connected';
  }

  public async initialize() {
    // Device name is empty after factory reset so we fallback
    this.name =
      (await this.client.read<string>('deviceName')) ||
      this.name ||
      this.device.name ||
      'Unnamed device';

    this.euid = await this.readEuidNumber();

    this.gitHash = await this.client.read<string>('gitHash');

    await this.writeBirthday();
    await this.terminateBondingAnimation();
  }

  private async readEuidNumber() {
    const euid = await this.client.read<number>('euid');

    return Array.from(new Uint8Array(euid))
      .reverse()
      .map(v => ('00' + v.toString(16)).slice(-2))
      .join('')
      .toUpperCase();
  }

  private async writeBirthday() {
    const param = this.pikaparam.param.getInApiThrow<number>('deviceBirthday');

    const dateOfBirth = await param.readOnce();

    if (dateOfBirth !== 4294967295) return;

    await param.set(Math.floor(Date.now() / 1000));
  }

  private async terminateBondingAnimation() {
    await this.writeCommand<number>('terminateBondingAnim', 1);
  }

  public async readDeviceAttributes() {
    const devBrightness = Buffer.from(
      await this.client.read<number[]>('ledBrightness'),
    );
    const attributes: DeviceAttributes = {
      boostTemperature: await this.client.read<number>(
        'heatCycle0BoostTempDelta',
      ),
      boostTime: await this.client.read<number>('heatCycle0BoostTimeDelta'),
      devBrightness,
      mode: await this.client.read<number>('readyModeCycle'),
      chargeState: await this.client.read<number>('battChargeState'),
      chargeEstimatedTimeToFull: await this.client.read<number>(
        'battChargeEstTimeToFull',
      ),
      batteryLevel: await this.readDeviceBattery(),
      batteryCapacity: await this.client.read<number>('batteryCapacity'),
      dateOfBirth: await this.client.read<number>('deviceBirthday'),
      utcTime: await this.client.read<number>('utcTime'),
      lanternTime: await this.client.read<number>('lanternRemainingTime'),
      selectedHeatCycle: await this.cycles.readSelectedHeatCycle(),
      dabTotalTime: await this.client.read<number>('totalHeatCycleTime'),
      ...(await this.readDabSummary()),
      stealth: await this.readStealthMode(),
      chamberType: await this.readChamberType(),
      stateElapsedTime: await this.client.read<number>('stateElapsedTime'),
      stateTotalTime: await this.client.read<number>('stateTotalTime'),
      operatingState: await this.client.read<number>('operatingState'),
      faultEndIndex: await this.client.read<number>('faultLogEnd'),
      currentTemperature: await this.client.read<number>('userHeaterTemp'),
      targetTemperature: await this.client.read<number>(
        'userHeaterTempCommand',
      ),
    };

    this.attributes = {...this.attributes, ...attributes};

    return attributes;
  }

  protected abstract readChamberType(): Promise<ChamberType>;

  public async watch() {
    await observables.reduce(async (promise, key) => {
      await promise;

      const setter = setters[key];

      await this.createWatch(key, (_, value: number) => {
        if (value == undefined) return;

        setter(this.attributes, value);
      }).catch(error => bleLog.error('Create watch failed.', {error}));
    }, Promise.resolve());
  }

  public async readDabSummary() {
    return {
      totalHeatCycles: await this.client.read<number>('totalHeatCycles'),
      dabsPerDay: await this.client.read<number>('dabsPerDay'),
      approxDabsRemaining: await this.client.read<number>(
        'approxDabsRemaining',
      ),
    };
  }

  public async readDeviceBattery() {
    return Math.round(await this.client.read<number>('battSoc'));
  }

  public supports(feature: PeakFeature): boolean {
    const featureFirmware = Constants.MINIMUM_FIRMWARE_VERSION[feature];

    return meetsMinimumFirmware(this.softwareRevision, featureFirmware);
  }

  public async updateBatterySaver(enabled: boolean) {
    if (!this.supports('BATTERY_PRESERVATION')) return;

    const percentage = await this.client.read<number>('maxBattSoc');
    const current = percentage < 100;

    if (enabled === current) return;

    await this.writeCommand<number>(
      'maxBattSoc',
      enabled ? Constants.BATTERY_PRESERVATION_PCT : 100,
    );
  }

  public async readStealthMode() {
    return this.client.read<number>('stealthMode');
  }

  public async readCurrentTemperature() {
    return this.client.read<number>('heaterTemp');
  }

  public async readTotalHeatCycles(): Promise<number> {
    return this.client.read<number>('totalHeatCycles');
  }

  public async readDabbingValues(): Promise<{
    state: OperatingState;
    settings: DeviceSettings;
  }> {
    const op = await this.client.read<number>('operatingState');

    let elapsedTime = 0;
    let totalTime = 0;

    const currentTemp = await this.client.read<number>('userHeaterTemp');
    const targetTemp = await this.client.read<number>('userHeaterTempCommand');

    // Prevent reading preheat elapsed/total times
    if (op === OperatingState.HEAT_CYCLE_ACTIVE) {
      totalTime = await this.client.read<number>('stateTotalTime');
      const elapsedTimeResponse =
        await this.client.read<number>('stateElapsedTime');

      if (Number.isFinite(elapsedTimeResponse)) {
        elapsedTime = elapsedTimeResponse;
      }
    }

    const activeLedArray = await this.leds.readActiveLedArray();

    const settings: DeviceSettings = {
      currentTemp,
      stateElapsedTime: elapsedTime,
      stateTotalTime: totalTime,
      targetTemp,
      isDabbingDiscoMode:
        !isNil(activeLedArray) &&
        // Compare with all party mode LED API 2 values except luma animation
        activeLedArray[Constants.TABLE_COLOR_BYTES.BRIGHTNESS] ===
          Constants.PARTY_MODE.T[Constants.TABLE_COLOR_BYTES.BRIGHTNESS] &&
        activeLedArray[Constants.TABLE_COLOR_BYTES.SPEED] ===
          Constants.PARTY_MODE.T[Constants.TABLE_COLOR_BYTES.SPEED] &&
        activeLedArray[Constants.TABLE_COLOR_BYTES.LED_API_TYPE_CODE] ===
          Constants.PARTY_MODE.T[
            Constants.TABLE_COLOR_BYTES.LED_API_TYPE_CODE
          ] &&
        activeLedArray[Constants.TABLE_COLOR_BYTES.PHASE_LOCK_NUMERATOR] ===
          Constants.PARTY_MODE.T[
            Constants.TABLE_COLOR_BYTES.PHASE_LOCK_NUMERATOR
          ] &&
        activeLedArray[Constants.TABLE_COLOR_BYTES.PHASE_LOCK_DENOMINATOR] ===
          Constants.PARTY_MODE.T[
            Constants.TABLE_COLOR_BYTES.PHASE_LOCK_DENOMINATOR
          ] &&
        activeLedArray[
          Constants.TABLE_COLOR_BYTES.COLOR_AND_OFFSET_ARRAY_INDICES
        ] ===
          Constants.PARTY_MODE.T[
            Constants.TABLE_COLOR_BYTES.COLOR_AND_OFFSET_ARRAY_INDICES
          ] &&
        activeLedArray[Constants.TABLE_COLOR_BYTES.COLOR_ARRAY_LENGTH] ===
          Constants.PARTY_MODE.T[
            Constants.TABLE_COLOR_BYTES.COLOR_ARRAY_LENGTH
          ],
    };

    return {
      state: op,
      settings,
    };
  }

  public async writeDeviceName(name: string) {
    await this.writeCommand<string>('deviceName', name);

    this.name = name;
  }

  public async writeDeviceReadyProfile(index: number) {
    await this.writeCommand<number>('readyModeCycle', index);
  }

  public async writeReadyModeOff() {
    await this.writeCommand<number>(
      'readyModeCycle',
      10, // NaN or out of range will turn off ready mode -- hopefully no Peak can support 10 in the near future
    );
  }

  public async writeDeviceBrightness(brightness: number) {
    await this.writeCommand<number[]>(
      'ledBrightness',
      [brightness, brightness, brightness, brightness], // [base, mid, glass, logo]
    );
  }

  public async writeStealthMode(mode: number) {
    await this.writeCommand<number>('stealthMode', mode);
  }

  public async writeMasterOff() {
    await this.writeCommand<number>('lanternStart', 0);
    await this.writeCommand<number>(
      'modeCmd',
      Constants.MODE_COMMAND.masterOff,
    );
  }

  public async addDabbingTime(time: number) {
    return this.writeCommand<number>('timeOverride', time);
  }

  public async addDabbingTemperature(
    temperature: number,
    unit: TemperatureUnit,
  ) {
    const temperatureInCelsius = Temperature.convertDifference(temperature, {
      from: unit,
      to: TemperatureUnit.Celsius,
    });

    return this.writeCommand<number>('tempOverride', temperatureInCelsius);
  }

  public async startLantern() {
    await this.writeCommand<number>('lanternStart', 1);
    await this.writeCommand<number>('lanternTime', Constants.LANTERN_TIME_SEC);
  }

  public async stopLantern() {
    await this.writeCommand<number>('lanternStart', 0);
  }

  public async writeUtcTime(newTime: number) {
    await this.writeCommand<number>('utcTime', newTime);
  }

  public async writeResetDevice() {
    await this.writeCommand<number>('factoryReset', 1);
  }

  protected getLedApiVersion(): 1 | 2 | 3 {
    const version = LED_API_VERSIONS.find(a =>
      this.product.features?.includes(a),
    );

    switch (version) {
      case 'led-api-1':
        return 1;
      case 'led-api-2':
        return 2;
      case 'led-api-3':
        return 3;
      default:
        throw new Error(
          `Unsupported led api version '${version}' for PeakDevice`,
        );
    }
  }

  public async writeLantern(options: WriteLanternOptions) {
    if (isMoodLight(options)) {
      await this.leds.writeLight({
        light: options,
        target: 'lantern',
        metadata: new Scratchpad({moodLight: options}),
      });

      return;
    }

    if (options.pattern === LightingPattern.PARTY_MODE) {
      await this.leds.setPartyMode();

      return;
    }

    await this.leds.writeLight({
      light: {
        color: options.color,
        pattern: options.pattern ?? LightingPattern.STEADY,
      },
      target: 'lantern',
      metadata: new Scratchpad({version: ScratchpadVersion.BLANK}),
    });
  }

  async startDabbing(index: number): Promise<void> {
    await this.writeCommand<number>('selectedHeatCycle', index);
    await this.writeCommand<number>(
      'modeCmd',
      Constants.MODE_COMMAND.heatCycleStart,
    );
  }

  public async writeBoostTemperature(temperature: number): Promise<void> {
    if (typeof temperature !== 'number' || temperature < 0) return;

    await this.writeCommand<number>('tempHeatCycleBoostTempDelta', temperature);

    for (let i = 0; i < 4; ++i) {
      await this.writeCommand<number>(
        `heatCycle${i as HeatCycleArrayIndex}BoostTempDelta` as const,
        temperature,
      );
    }
  }

  public async writeBoostDuration(duration: number): Promise<void> {
    if (typeof duration !== 'number' || duration < 0) return;

    await this.writeCommand<number>('tempHeatCycleBoostTimeDelta', duration);

    for (let i = 0; i < 4; i++) {
      await this.writeCommand<number>(
        `heatCycle${i as HeatCycleArrayIndex}BoostTimeDelta` as const,
        duration,
      );
    }
  }

  public async subscribeToLogs(o: StartPeakLogCollectorOptions) {
    this.logCollector.start(o);
  }

  public async disconnect(): Promise<void> {
    bleLog.info(`Peak disconnect.`);

    this.logCollector.stop();
    await this.cleanupWatches().catch(() => void 0);

    await this.pikaparam.conn.disconnect();

    await firstValueFrom(
      this.pikaparam.conn.deviceStateObs.pipe(
        filter(v => v === 'disconnected'),
        timeout({
          each: 5000,
          with: () =>
            throwError(
              () => new Error("Peak didn't disconnect after 5 seconds."),
            ),
        }),
      ),
    );
  }

  protected tryGetApi<K extends keyof P>(key: K) {
    bleLog.info(`getInApiTry(${String(key)})`);

    // @ts-ignore
    return this.pikaparam.param.getInApiTry<P[K]>(key);
  }

  private async createWatch<K extends keyof P>(
    property: K,
    callback?: (path: K, value: any) => void,
  ) {
    const watch = this.watchers.get(property);
    const callbacks = watch?.callbacks ?? new Set();

    // @ts-ignore
    callbacks.add(callback);

    if (watch) return;

    const clean = await this.watchCommand(property, value => {
      Array.from(callbacks.values()).forEach(callback =>
        callback(property as string, value),
      );
    });

    this.watchers.set(property, {clean, callbacks});
  }

  private async watchCommand<K extends keyof P>(
    key: K,
    callback: (value: P[K]) => void,
  ): Promise<() => void> {
    bleLog.info(`getInApiThrow(${key.toString()}).startObs`, {key});

    const observable = await this.pikaparam.param
      .getInApiThrow(key.toString())
      .startObs();

    const subscription = observable
      .pipe(catchError(() => EMPTY))
      .pipe(distinctUntilChanged())
      .subscribe(value => callback(value as P[K]));

    return () => subscription.unsubscribe();
  }

  private async cleanupWatches(): Promise<void> {
    bleLog.info(`Clean up watches.`);

    Array.from(this.watchers.entries()).forEach(([, {clean}]) => clean());

    this.watchers.clear();
  }

  private async writeCommand<V extends CodecTypes>(key: string, value: V) {
    const result = await this.client.write(key, value);

    if (key in setters) {
      const setter = setters[key as keyof typeof setters];

      // @ts-ignore
      setter(this.attributes, result);
    }
  }
}
